// SETTINGS V2 — Guest passes list (TA3). Sits under the Team table on the v2 Team
// page. Lists issued guest passes (GET /v1/users/guest-passes) with the row kebab
// (Nový PIN / Poslať znova / Zrušiť), all gated Machinery:Write. Hosts its own
// success/error toasts bound to the guestPasses slice. v2 atoms + scoped
// CSS-var tokens; no MUI/sx/dt/Tailwind (ActionToast + SettingsV2ConfirmDialog are
// shared v2 components). SEC-REQ-2: fetch only in the mount effect.
//
// GP-MENU / GP-COLLAPSE:
//  • Actions moved from inline text buttons into GuestPassRowV2Menu.
//  • "Nový PIN" = confirm → source-matching thunk → show-once handoff dialog. The
//    route is picked from the ROW's source; the client never adapts (the server
//    refuses a cross-route id with 1210).
//  • Terminal rows collapse behind "Ukončené (N)". The partition is
//    state-driven only — see guestPassPartition.ts for the Active-can-never-be-
//    hidden guarantee. There is no hide/archive affordance anywhere.
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import Loader from "../../../../shared/components/Loader";
import ActionToast from "../../../../shared/components/ActionToast";
import { getNotificationMessage } from "../../../../shared/utils/notificationUtils";
import { NotificationMessage } from "../../../../shared/models/models";
import { GuestPass, GuestPassSource } from "../../models/guestPassModels";
import {
  getGuestPasses,
  revokeGuestPass,
  reissueGuestPass,
  regenerateGuestPassPin,
  regenerateDemoPassPin,
  clearGuestPassError,
  clearGuestPassSuccess,
  clearLastRegeneratedPass,
} from "../../guestPassesSlice";
import { SCard } from "../atoms";
import SettingsV2ConfirmDialog from "../dialogs/SettingsV2ConfirmDialog";
import RegeneratePinDialogV2 from "../dialogs/RegeneratePinDialogV2";
import GuestPassRowV2 from "./GuestPassRowV2";
import GuestPassTerminalGroupV2 from "./GuestPassTerminalGroupV2";
import { partitionGuestPasses } from "./guestPassPartition";

const GuestPassListV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { canWrite } = usePermission(Modules.Machinery);

  const { passes, loading, success, error, lastRegeneratedPass } = useAppSelector(
    (s) => s.guestPasses
  );

  const [revokeTarget, setRevokeTarget] = useState<GuestPass | null>(null);
  const [regenTarget, setRegenTarget] = useState<GuestPass | null>(null);
  // Which route was called — the handoff variant falls back to this if the
  // response omits `source` (degrade, never crash).
  const [regenVariant, setRegenVariant] = useState<"demo" | "guest">("guest");

  // Mount fetch.
  useEffect(() => {
    dispatch(getGuestPasses());
  }, [dispatch]);

  const handleRevoke = async () => {
    const target = revokeTarget;
    setRevokeTarget(null);
    if (!target) return;
    const result = await dispatch(revokeGuestPass(target.id));
    if (revokeGuestPass.fulfilled.match(result)) {
      dispatch(getGuestPasses());
    }
  };

  const handleResend = async (pass: GuestPass) => {
    const result = await dispatch(reissueGuestPass(pass.id));
    if (reissueGuestPass.fulfilled.match(result)) {
      dispatch(getGuestPasses());
    }
  };

  // Confirmed "Nový PIN". The pass source picks the endpoint — a demo row NEVER
  // goes through the guest route (Manager-callable) and vice versa.
  const handleRegenerate = async () => {
    const target = regenTarget;
    setRegenTarget(null);
    if (!target) return;
    const isDemo = target.source === GuestPassSource.Demo;
    setRegenVariant(isDemo ? "demo" : "guest");
    // On fulfilled the slice sets lastRegeneratedPass, which opens the handoff
    // dialog below. On rejected the shared error toast fires.
    await dispatch(isDemo ? regenerateDemoPassPin(target.id) : regenerateGuestPassPin(target.id));
  };

  // Closing the handoff drops the PIN from the store and refreshes the list (the
  // old row is now terminal, the new one Active).
  const handleRegenHandoffClose = () => {
    dispatch(clearLastRegeneratedPass());
    dispatch(getGuestPasses());
  };

  const isRegenTargetDemo = regenTarget?.source === GuestPassSource.Demo;
  const handoffVariant: "demo" | "guest" = lastRegeneratedPass?.source
    ? lastRegeneratedPass.source === GuestPassSource.Demo
      ? "demo"
      : "guest"
    : regenVariant;

  const { active, terminal } = partitionGuestPasses(passes);

  const renderRow = (pass: GuestPass) => (
    <GuestPassRowV2
      key={pass.id}
      pass={pass}
      canWrite={canWrite}
      onResend={handleResend}
      onRegeneratePin={setRegenTarget}
      onRevoke={setRevokeTarget}
    />
  );

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div style={{ display: "grid", placeItems: "center", padding: "36px 0" }}>
        <Loader />
      </div>
    );
  } else if (!passes?.length) {
    body = (
      <div style={{ padding: "0 16px 16px" }}>
        <div
          style={{
            border: "1px dashed var(--border)",
            borderRadius: 8,
            padding: "28px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
            {t("guestPass.emptyTitle") as string}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
            {t("guestPass.emptyHint") as string}
          </div>
        </div>
      </div>
    );
  } else {
    body = (
      <div style={{ width: "100%" }}>
        {active.map(renderRow)}
        {/* Terminal rows exist but nothing is actionable — say so, otherwise the
            card reads as broken above the collapsed group. */}
        {active.length === 0 && (
          <div
            style={{
              padding: "12px 16px",
              borderTop: "1px solid var(--border-soft)",
              fontSize: 12.5,
              color: "var(--muted)",
            }}
          >
            {t("guestPass.emptyActive") as string}
          </div>
        )}
        <GuestPassTerminalGroupV2 count={terminal.length}>
          {terminal.map(renderRow)}
        </GuestPassTerminalGroupV2>
      </div>
    );
  }

  return (
    <>
      <ActionToast
        open={!!success}
        variant="success"
        message={getNotificationMessage(success, t as (key: string, params?: any) => string)}
        onClose={() => dispatch(clearGuestPassSuccess())}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={t(NotificationMessage.error(error).message) as string}
        onClose={() => dispatch(clearGuestPassError())}
        duration={6000}
      />

      <SCard
        title={t("guestPass.sectionTitle") as string}
        hint={t("guestPass.sectionHint") as string}
        bleed
        style={{ animation: "sv2-card-reveal 0.4s ease-out both" }}
      >
        {body}
      </SCard>

      {/* Demo passes get their own wording: revoking cuts machine data on the next
          request across the nine liveness-checking services, but MIDAS chat runs on
          the Account service and keeps answering until the token expires — "keeps
          talking but stops seeing". Promising an instant kill here would be false. */}
      <SettingsV2ConfirmDialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title={
          (revokeTarget?.source === GuestPassSource.Demo
            ? t("guestPass.revokeTitleDemo")
            : t("guestPass.revokeTitle")) as string
        }
        text={
          (revokeTarget?.source === GuestPassSource.Demo
            ? t("guestPass.revokeTextDemo", {
                guestEmail: revokeTarget?.guestEmail ?? "",
                expiresAt: revokeTarget?.expiresAt
                  ? dayjs(revokeTarget.expiresAt).format("DD.MM.YYYY HH:mm")
                  : "",
              })
            : t("guestPass.revokeText", {
                guestEmail: revokeTarget?.guestEmail ?? "",
              })) as string
        }
        confirmLabel={t("guestPass.revokeConfirm") as string}
        cancelLabel={t("guestPass.revokeCancel") as string}
        danger
        onConfirm={handleRevoke}
      />

      {/* "Nový PIN" confirm. [SEC-REQ GP-5] Both texts state the three facts the
          operator must have BEFORE acting: the old PIN dies immediately, no email
          is sent, and sessions already signed in keep running until they expire —
          "Zrušiť" is the immediate-kill tool. */}
      <SettingsV2ConfirmDialog
        open={!!regenTarget}
        onClose={() => setRegenTarget(null)}
        title={t("guestPass.regenConfirmTitle") as string}
        text={
          (isRegenTargetDemo
            ? t("guestPass.regenConfirmTextDemo")
            : t("guestPass.regenConfirmText", {
                guestEmail: regenTarget?.guestEmail ?? "",
              })) as string
        }
        confirmLabel={t("guestPass.regenConfirm") as string}
        cancelLabel={t("guestPass.revokeCancel") as string}
        onConfirm={handleRegenerate}
      />

      <RegeneratePinDialogV2
        open={!!lastRegeneratedPass}
        pass={lastRegeneratedPass}
        variant={handoffVariant}
        onClose={handleRegenHandoffClose}
      />
    </>
  );
};

export default GuestPassListV2;
